import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { LAYOUTS } from '@/components/admin/GalleryRowsEditor';
import { useEditMode } from '@/context/EditModeContext';
import PhotoRowsInline from '@/components/editable/PhotoRowsInline';

const layoutBy = (id) => LAYOUTS.find(l => l.id === id) || LAYOUTS[0];

/**
 * GalleryRows (public)
 * --------------------
 * Renders the row-based mixed-layout gallery for a given scope ("main" or "womens").
 * Each row uses the admin-selected layout; empty slots are skipped so partial
 * rows still look intentional.
 */
// wrapClass/innerClass/children render only when there is content (or in edit mode), so empty zones vanish for visitors.
export default function GalleryRows({ scope = 'main', wrapClass = '', innerClass = '', children }) {
  const { editMode } = useEditMode();
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get('/site/gallery-rows', { params: { scope } })
    .then(r => setRows((r.data || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))))
    .catch(() => setRows([])), [scope]);
  useEffect(() => { load(); }, [load]);
  if (rows === null) return null;
  const hasPhotos = rows.some(r => (r.images || []).some(Boolean));
  if (!editMode && !hasPhotos) return null;
  const body = editMode
    ? <PhotoRowsInline scope={scope} rows={rows} reload={load} />
    : <div className="space-y-6">{rows.map(r => <Row key={r.id} row={r} />)}</div>;
  return (
    <div className={wrapClass} data-testid={`gallery-rows-${scope}`}>
      <div className={innerClass}>{children}{body}</div>
    </div>
  );
}

function Row({ row }) {
  const lo = layoutBy(row.layout);
  const imgs = (row.images || []).filter(Boolean);
  if (imgs.length === 0) return null;

  if (row.layout === 'big-left-2') {
    const [big, s1, s2] = row.images || [];
    return (
      <div data-testid={`gallery-row-${row.id}`} className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:row-span-2 md:col-span-2 md:aspect-[4/5] aspect-[3/4] overflow-hidden rounded-lg">
          {big && <img src={big} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
          {[s1, s2].filter(Boolean).map((u, i) => (
            <div key={i} className="aspect-[4/3] overflow-hidden rounded-lg">
              <img src={u} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (row.layout === '2-big-right') {
    const [s1, s2, big] = row.images || [];
    return (
      <div data-testid={`gallery-row-${row.id}`} className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
          {[s1, s2].filter(Boolean).map((u, i) => (
            <div key={i} className="aspect-[4/3] overflow-hidden rounded-lg">
              <img src={u} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
          ))}
        </div>
        <div className="md:row-span-2 md:col-span-2 md:aspect-[4/5] aspect-[3/4] overflow-hidden rounded-lg">
          {big && <img src={big} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />}
        </div>
      </div>
    );
  }
  // Equal grids (3-equal, 2-equal, solo, 4-equal)
  return (
    <div data-testid={`gallery-row-${row.id}`} className={`grid ${lo.cols} gap-3`}>
      {imgs.map((u, i) => (
        <div key={i} className={`overflow-hidden rounded-lg ${row.layout === 'solo' ? 'aspect-[16/9]' : 'aspect-[3/4]'}`}>
          <img src={u} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
        </div>
      ))}
    </div>
  );
}
